import {
  selectAxis,
  selectLineItem,
  updateTaxonomyTagCache,
} from "@/apis/taxonomy/route";
import queries, { excelQueries } from "@/queries";
import { useMappingStore } from "@/stores/mapping";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTableId } from "../hooks";

export const useMappingDetailValue = () => {
  const selectedFact = useMappingStore((state) => state.fact.selected);

  const { data: cellValue } = useQuery({
    ...excelQueries.cellValue(selectedFact?.table?.id!, selectedFact?.address!),
    enabled: !!selectedFact?.table?.id && !!selectedFact?.address,
  });

  return cellValue;
};

interface SelectItemParams {
  parentKey: string | null;
  tagKey: string;
  roleKey: string;
  originalRoleKey: string | null;
  patchTagCache?: boolean;
  onSuccessCallback?: () => void;
}

export const useSelectItem = (params: SelectItemParams) => {
  const {
    parentKey,
    tagKey,
    roleKey,
    originalRoleKey,
    onSuccessCallback,
    patchTagCache = true,
  } = params;
  const dimension = useMappingStore((state) => state.dimension.dimension);
  const selectedFact = useMappingStore((state) => state.fact.selected?.address);
  const {
    data: { workbookId, tableId },
  } = useTableId();
  const queryClient = useQueryClient();
  const {
    mutate: selectItemMutate,
    mutateAsync: selectItemMutateAsync,
    isPending: isSelectingItem,
  } = useMutation({
    mutationFn: async () => {
      if (!workbookId || !roleKey || !tagKey || !tableId) {
        throw new Error("Required data is missing");
      }

      if (dimension === "LINE_ITEM") {
        return selectLineItem(workbookId, roleKey, {
          tagKey: tagKey,
          tableId: tableId,
        });
      } else {
        return selectAxis(workbookId, roleKey, {
          axisKey: parentKey || "",
          tagKey: tagKey,
          originalRoleKey: originalRoleKey || "",
          tableId: tableId,
        });
      }
    },
    onSuccess: () => {
      if (patchTagCache) {
        updateTagCacheMutate();
      }
    },
  });

  const {
    mutate: updateTagCacheMutate,
    mutateAsync: updateTagCacheMutateAsync,
    isPending: isUpdatingCache,
  } = useMutation({
    mutationFn: async () => {
      if (!workbookId || !tableId || !selectedFact || !tagKey) {
        throw new Error("Required data is missing");
      }
      return await updateTaxonomyTagCache({
        workbookId,
        tableId,
        address: selectedFact,
        dimension,
        tagKey,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queries
          .workbook(workbookId!)
          ._ctx.tables(tableId!)
          ._ctx.taxonomy().queryKey,
      });
      toast.success("태그가 추가되었습니다.");
      onSuccessCallback?.();
    },
  });

  const handleSelectItem = () => {
    try {
      selectItemMutate();
    } catch (error) {
      console.error(`Failed to select ${dimension.toLowerCase()}:`, error);
    }
  };

  const handleSelectItemAsync = async () => {
    try {
      await selectItemMutateAsync();
      if (patchTagCache) {
        await updateTagCacheMutateAsync();
      }
    } catch (error) {
      console.error(`Failed to select ${dimension.toLowerCase()}:`, error);
    }
  };

  return {
    handleSelectItem,
    handleSelectItemAsync,
    isLoading: isSelectingItem || isUpdatingCache,
  };
};
